import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AbstractField } from '../../models/field-configs/abstract-field';
import { FieldInputTimeConfig } from '../../models/field-configs/field-input-time-config.interface';
import { TimepickerHourFormat } from '../../../../components/cmx-timepicker/cmx-timepicker.component';
import * as moment from 'moment';
import { getObservableFromAsyncValueSafe } from '@ordersnproducts/modules/shared/models/async-value';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { TimeIntervalService } from '@ordersnproducts/modules/shared/services/time-interval.service';
import { TimepickerSteps } from '@ordersnproducts/modules/shared/components/cmx-timepicker/cmx-timepicker.component';

@Component({
  selector: 'form-input',
  styleUrls: ['form-input-time.component.scss'],
  templateUrl: 'form-input-time.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormInputTimeComponent extends AbstractField implements OnInit, OnDestroy {
  config: FieldInputTimeConfig;
  group: UntypedFormGroup;
  hourFormat: TimepickerHourFormat = TimepickerHourFormat.format24;
  min: moment.Moment;
  max: moment.Moment;
  minStr: string;
  minuteStep: number = TimepickerSteps.minutes;
  propagateOnChange = true;
  writable = false;
  successLabel: string;
  startWithDefaultTime = true;
  presetTime: moment.Moment;
  controlStatus$ = new BehaviorSubject('regular');
  controlStatusMessage$ = new BehaviorSubject('');
  disclaimerText: string;
  nextDay: boolean;
  maxDaysAhead: number;
  initialDate: string;

  public timeInterval$ = this.timeIntervalService.getTimeInterval();

  fieldControl: UntypedFormControl;
  isRequired$ = new BehaviorSubject(false);

  disabled = false;
  blocked = false;
  cycleCurrentDate = true;

  private _ngDestroyed = new Subject();

  constructor(
    private timeIntervalService: TimeIntervalService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.config.hasOwnProperty('cycleCurrentDate')) {
      this.cycleCurrentDate = this.config.cycleCurrentDate;
    }

    if (this.config.hasOwnProperty('hourFormat')) {
      this.hourFormat = this.config.hourFormat;
    }

    if (this.config.hasOwnProperty('minuteStep')) {
      this.minuteStep = this.config.minuteStep;
    }

    if (this.config.hasOwnProperty('disclaimerText')) {
      getObservableFromAsyncValueSafe(this.config.disclaimerText)
        .pipe(distinctUntilChanged(), takeUntil(this._ngDestroyed))
        .subscribe((text: string) => {
          this.disclaimerText = text;

          this.cdr.detectChanges();
        });
    }

    if (this.config.min) {
      if (this.config.min instanceof moment) {
        this.min = this.config.min as moment.Moment;

        this.cdr.detectChanges();
      } else {
        getObservableFromAsyncValueSafe(this.config.min)
          .pipe(
            distinctUntilChanged((prev, current) => {
              return moment(prev).format() === moment(current).format();
            }),
            takeUntil(this._ngDestroyed),
          )
          .subscribe((time) => {
            this.min = time;

            this.cdr.detectChanges();
          });
      }
    }

    if (this.config.max) {
      if (this.config.max instanceof moment) {
        this.max = this.config.max as moment.Moment;

        this.cdr.detectChanges();
      } else {
        getObservableFromAsyncValueSafe(this.config.max)
          .pipe(
            distinctUntilChanged((prev, current) => {
              return moment(prev).format() === moment(current).format();
            }),
            takeUntil(this._ngDestroyed),
          )
          .subscribe((time) => {
            this.max = time;

            this.cdr.detectChanges();
          });
      }
    }

    if (this.config.hasOwnProperty('nextDay')) {
      getObservableFromAsyncValueSafe(this.config.nextDay)
        .pipe(
          distinctUntilChanged((prev, current) => {
            return prev === current;
          }),
          takeUntil(this._ngDestroyed),
        )
        .subscribe((value: boolean) => {
          this.nextDay = value;
          this.cdr.detectChanges();
        });
    }

    if (this.config.hasOwnProperty('maxDaysAhead')) {
      getObservableFromAsyncValueSafe(this.config.maxDaysAhead)
        .pipe(
          distinctUntilChanged((prev, current) => {
            return prev === current;
          }),
          takeUntil(this._ngDestroyed),
        )
        .subscribe((days) => {
          this.maxDaysAhead = days;
          this.cdr.detectChanges();
        });
    }

    if (this.config.hasOwnProperty('initialDate')) {
      getObservableFromAsyncValueSafe(this.config.initialDate)
        .pipe(
          distinctUntilChanged((prev, current) => {
            return prev === current;
          }),
          takeUntil(this._ngDestroyed),
        )
        .subscribe((date) => {
          this.initialDate = date;
          this.cdr.detectChanges();
        });
    }

    if (this.config.disabled) {
      if (typeof this.config.disabled === 'boolean') {
        this.disabled = this.config.disabled;
      } else {
        const disabled$ = getObservableFromAsyncValueSafe(this.config.disabled).pipe(
          takeUntil(this._ngDestroyed),
          map((value) => {
            this.disabled = value;
          }),
        );
        disabled$.subscribe();
      }
    }

    if (this.config.hasOwnProperty('propagateOnChange')) {
      this.propagateOnChange = this.config.propagateOnChange;
    }

    if (this.config.hasOwnProperty('writable')) {
      this.writable = this.config.writable;
    }

    if (this.config.hasOwnProperty('successLabel')) {
      this.successLabel = this.config.successLabel;
    }

    if (this.config.hasOwnProperty('startWithDefaultTime')) {
      this.startWithDefaultTime = this.config.startWithDefaultTime;
    }

    if (this.config.blocked) {
      getObservableFromAsyncValueSafe(this.config.blocked)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((blocked) => {
          this.blocked = blocked;
          this.cdr.detectChanges();
        });
    }

    if (this.config.presetTime) {
      getObservableFromAsyncValueSafe(this.config.presetTime)
        .pipe(
          distinctUntilChanged((prev, current) => {
            return moment(prev).format() === moment(current).format();
          }),
          takeUntil(this._ngDestroyed),
        )
        .subscribe((time) => {
          this.presetTime = time;
        });
    } else {
      this.config.min = moment().add(10, 'minutes');
      this.minStr = this.config.min.format('HH:mm');
    }

    this.fieldControl = this.getControl() as UntypedFormControl;
    if (!this.presetTime && this.startWithDefaultTime) {
      const m = moment();
      this.presetTime =
        m.minute() || m.second() || m.millisecond() ? m.add(1, 'hour').startOf('hour') : m.startOf('hour');
    }
    if (this.fieldControl.value == undefined && this.presetTime != undefined) {
      this.fieldControl.setValue(this.presetTime.toLocaleString());
    }
    this.isRequired$.next(this.hasRequiredField(this.group.get(this.config.name)));

    this.fieldControl.statusChanges
      .pipe(
        map(() => {
          if (this.fieldControl.invalid && this.fieldControl.hasError('required') && !this.fieldControl.untouched) {
            this.controlStatusMessage$.next('forms.required');
            this.controlStatus$.next('error');
          } else if (this.successLabel && !this.fieldControl.invalid) {
            this.controlStatusMessage$.next(this.successLabel);
            this.controlStatus$.next('success');
          } else if (this.fieldControl.invalid && this.fieldControl.hasError('warning')) {
            this.controlStatusMessage$.next('');
            this.controlStatus$.next('warning');
          } else {
            this.controlStatusMessage$.next('');
            this.controlStatus$.next('regular');
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._ngDestroyed.next();
    this._ngDestroyed.complete();
  }
}
